import React, { useState, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import IconButton from 'apollo-react/components/IconButton';
import Close from 'apollo-react-icons/Close';
import PaperPlane from 'apollo-react-icons/PaperPlane';
import styles from './QuickCommentInput.module.css';

/**
 * QuickCommentInput - Inline quick comment input component
 *
 * @param {number|string} subformId - Subform ID
 * @param {string} fieldId - Field ID
 * @param {Function} onAddComment - Callback to add comment, returns the new comment object
 * @param {Function} onClose - Callback when input is closed
 * @param {Function} onCommentAdded - Callback with the newly added comment object
 */
const QuickCommentInput = ({
  subformId,
  fieldId,
  onAddComment,
  onClose,
  onCommentAdded,
}) => {
  const { t } = useTranslation();
  const [value, setValue] = useState('');
  const [isSending, setIsSending] = useState(false);

  const handleSend = useCallback(async () => {
    const trimmed = String(value || '').trim();
    if (!trimmed || isSending) return;

    setIsSending(true);
    try {
      const newComment = await onAddComment({
        comment: trimmed,
        subFormId: subformId,
        fieldId,
      });
      setValue('');
      if (onCommentAdded && newComment) {
        onCommentAdded(newComment);
      }
      onClose();
    } catch (error) {
      console.error('Error sending comment:', error);
    } finally {
      setIsSending(false);
    }
  }, [
    value,
    isSending,
    onAddComment,
    subformId,
    fieldId,
    onCommentAdded,
    onClose,
  ]);

  const handleKeyDown = useCallback(
    (e) => {
      if (e.key === 'Enter' && !e.shiftKey) {
        e.preventDefault();
        handleSend();
      } else if (e.key === 'Escape') {
        onClose();
      }
    },
    [handleSend, onClose]
  );

  return (
    <div className={styles.container}>
      <input
        className={styles.input}
        value={value}
        onChange={(e) => setValue(e.target.value)}
        onKeyDown={handleKeyDown}
        placeholder={t('enter_comment_placeholder')}
        autoFocus
        disabled={isSending}
      />
      <div className={styles.actions}>
        <IconButton
          size="small"
          className={styles.iconButton}
          onClick={handleSend}
          title={t('fieldActions.send')}
          disabled={isSending}
        >
          <PaperPlane className={styles.icon} />
        </IconButton>
        <IconButton
          size="small"
          className={styles.iconButton}
          onClick={onClose}
          title={t('cancel')}
          disabled={isSending}
        >
          <Close className={styles.icon} />
        </IconButton>
      </div>
    </div>
  );
};

export default QuickCommentInput;
